import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePoolAuditLogs } from '@/api/audit-logs'
import { useIdempotencyKey } from '@/api/idempotency'
import { poolLabel, useArchivePool, usePool, useUnarchivePool } from '@/api/pools'
import type { Pool, PoolDetail } from '@/api/pools'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { poolErrorMessage } from '@/components/materials/error-message'
import { PoolInBomLinks } from '@/components/materials/pool-in-bom-links'
import { PoolMovements } from '@/components/materials/pool-movements'
import { PoolVariants } from '@/components/materials/pool-variants'
import { MaterialsActions } from '@/components/materials/materials-actions'
import { PoolFlags } from '@/components/materials/pool-flags'
import { formatCostPerUnit, formatMoney, formatQty } from '@/components/materials/pool-format'
import { RecordHistory } from '@/components/record-history/record-history'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// Pool detail (UI-SCREENS M-03): balance, derived cost per unit and flags, the movement history
// (#65), the Variants mapped to a filament Pool (#102), plus Edit and Archive and the History
// section (#69) for the Owner. Staff see the same figures, read only.
export const Route = createFileRoute('/w/$slug/materials/pools/$poolId/')({
  component: PoolDetail,
  staticData: { breadcrumb: 'materials.detail.title' },
})

function PoolDetail() {
  const { t } = useTranslation()
  const { slug, poolId } = Route.useParams()
  const workspace = useWorkspace(slug)
  const pool = usePool(slug, Number(poolId))
  const isOwner = workspace.data?.role === 'owner'
  const history = usePoolAuditLogs(slug, Number(poolId), { enabled: isOwner })

  if (pool.isPending) return <LoadingSkeleton variant="page" />
  if (pool.isError || !pool.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/materials" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('materials.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{poolLabel(pool.data)}</h1>
      <PoolFlags pool={pool.data} />
      <BalanceCard pool={pool.data} />
      <PoolMovements slug={slug} poolId={pool.data.id} unit={pool.data.unit} isOwner={isOwner} />
      {pool.data.kind === 'filament' ? (
        <PoolVariants slug={slug} mappings={pool.data.variant_mappings} isOwner={isOwner} />
      ) : null}
      <MaterialsActions slug={slug} poolId={pool.data.id} />
      <Actions slug={slug} pool={pool.data} />
      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

function BalanceCard({ pool }: { pool: Pool }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('materials.detail.balance')}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
          <Figure label={t('materials.columns.qty')} value={`${formatQty(pool.qty)} ${pool.unit}`} />
          <Figure
            label={t('materials.columns.costPerUnit')}
            value={pool.cost_per_unit === null ? t('materials.noCost') : `Rp ${formatCostPerUnit(pool.cost_per_unit)}`}
          />
          <Figure label={t('materials.columns.totalValue')} value={`Rp ${formatMoney(pool.total_value)}`} />
          <Figure
            label={t('materials.columns.minQty')}
            value={pool.min_qty === null ? t('materials.noMinimum') : `${formatQty(pool.min_qty)} ${pool.unit}`}
          />
        </dl>
      </CardContent>
    </Card>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function Actions({ slug, pool }: { slug: string; pool: PoolDetail }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const archive = useArchivePool()
  const unarchive = useUnarchivePool()
  const archiveKey = useIdempotencyKey()
  const unarchiveKey = useIdempotencyKey()
  const [confirming, setConfirming] = useState<'archive' | 'unarchive' | null>(null)
  const [notice, setNotice] = useState('')
  const name = poolLabel(pool)
  // The Variants (not archived) that will show "Perlu warna" once this Pool is archived.
  const needColor = [
    ...new Set(pool.variant_mappings.filter((mapping) => !mapping.variant_archived).map((m) => `${m.sku} · ${m.name}`)),
  ]
  const busy = archive.isPending || unarchive.isPending
  const error = archive.isError ? archive.error : unarchive.isError ? unarchive.error : null

  function run(action: 'archive' | 'unarchive') {
    setConfirming(null)
    const key = action === 'archive' ? archiveKey : unarchiveKey
    const mutation = action === 'archive' ? archive : unarchive
    mutation.mutate(
      { params: { path: { slug, pool_id: pool.id }, header: key.headerFor({ id: pool.id }) } },
      {
        onSuccess: () => setNotice(t(`materials.archive.${action}d`, { name })),
        onSettled: (_data, mutationError) => key.settle(mutationError),
      },
    )
  }

  if (!pool.can.update && !pool.can.archive && !pool.can.unarchive) return null

  return (
    <div className="flex flex-col gap-3">
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {notice}
      </p>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {error ? poolErrorMessage(t, error) : ''}
      </p>
      <PoolInBomLinks slug={slug} error={error} />
      <div className="flex flex-wrap gap-2">
        {pool.can.update ? (
          <Button
            variant="outline"
            className="h-11"
            onClick={() =>
              navigate({ to: '/w/$slug/materials/pools/$poolId/edit', params: { slug, poolId: String(pool.id) } })
            }
          >
            {t('materials.form.editTitle')}
          </Button>
        ) : null}
        {pool.can.archive ? (
          <Button variant="ghost" className="h-11 text-destructive" disabled={busy} onClick={() => setConfirming('archive')}>
            {t('materials.archive.archive')}
          </Button>
        ) : null}
        {pool.can.unarchive ? (
          <Button variant="outline" className="h-11" disabled={busy} onClick={() => setConfirming('unarchive')}>
            {t('materials.archive.unarchive')}
          </Button>
        ) : null}
      </div>

      {confirming ? (
        <AlertDialog open onOpenChange={(open) => (open ? undefined : setConfirming(null))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(confirming === 'archive' ? 'materials.archive.confirmTitle' : 'materials.archive.confirmUnarchiveTitle', {
                  name,
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(confirming === 'archive' ? 'materials.archive.confirmBody' : 'materials.archive.confirmUnarchiveBody')}
                {/* A Pool counted to 0 may still carry value; the confirmation says so (#64). */}
                {confirming === 'archive' && pool.total_value !== 0
                  ? ` ${t('materials.archive.confirmValue', { value: formatMoney(pool.total_value) })}`
                  : ''}
                {/* Archiving stays allowed; the Variants mapped to the Pool will need color (#102). */}
                {confirming === 'archive' && needColor.length > 0
                  ? ` ${t('materials.archive.confirmVariants', { count: needColor.length, variants: needColor.join(', ') })}`
                  : ''}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-11">{t('materials.archive.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="h-11" onClick={() => run(confirming)}>
                {t('materials.archive.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  )
}
